import type { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

interface PageHeaderProps {
  breadcrumb?: ReactNode;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  /** Extra content below the subtitle (e.g., badges, stats) */
  meta?: ReactNode;
  /** Controls margin below the rule divider. */
  ruleSpacing?: 'mb-6' | 'mb-8';
  /** If true, omit the rule divider entirely. */
  noRule?: boolean;
  className?: string;
}

export function PageHeader({
  breadcrumb,
  eyebrow,
  title,
  subtitle,
  action,
  meta,
  ruleSpacing = 'mb-6',
  noRule = false,
  className,
}: PageHeaderProps) {
  return (
    <>
      <header
        className={cn(
          'mb-6',
          action && 'flex items-end justify-between gap-4',
          className,
        )}
      >
        <div>
          {breadcrumb && (
            <nav
              aria-label="Breadcrumb"
              className="mb-3 text-[0.75rem] text-[var(--color-ink-muted)]"
            >
              {breadcrumb}
            </nav>
          )}
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h1 className="font-display-tight mt-2 text-4xl font-semibold tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
              {subtitle}
            </p>
          )}
          {meta}
        </div>
        {action}
      </header>
      {!noRule && <div className={cn('rule', ruleSpacing)} />}
    </>
  );
}
